import { Controller } from "@hotwired/stimulus"
import {dia, shapes, V} from '@joint/plus'
import tippy from 'tippy.js'

// Connects to data-controller="network-map"
export default class extends Controller {
  static values = {
    network: Object,
    previewAssetPath: String,
    previewNetworkPath: String,
  }
  
  connect() {
    this.initializeProperties()
    this.initializePaper()
    this.setupEventListeners()
    this.draw()
  }

  disconnect() {
    this.hideNotSentTooltip()

    document.body.style.cursor = ''
    this.parentContainer.style.cursor = ''

    document.removeEventListener('mousemove', this._onMouseMove)
    document.removeEventListener('mouseup',   this._onMouseUp)
    document.removeEventListener('touchmove', this._onTouchMove)
    document.removeEventListener('touchend',  this._onTouchEnd)
    window.removeEventListener('resize', this._onResize)

    this.parentContainer.removeEventListener('scroll', this._onScroll)

    if (this.paper?.el) {
      this.paper.el.removeEventListener('touchstart', this._onTouchStart)
      this.paper.el.removeEventListener('wheel',      this._onWheel)
    }

    this.layoutObserver?.disconnect()
  }

  // ===== INITIALIZATION =====
  
  initializeProperties() {
    this.nodeSpacing = 100
    this.parentContainer = this.element
    this.graph = new dia.Graph()
    this.graphScale = 1
    
    // Touch interaction state
    this.isDragging = false
    this.isZooming = false
    this.lastTouchDistance = 0
    this.lastTouchCenter = { x: 0, y: 0 }
    this.lastZoomTime = 0
    
    // Tooltip state
    this.currentTooltip = null

    // Deferred fit when the container is not yet laid out (e.g. hidden turbo frame)
    this.pendingFitToView = false
  }

  initializePaper() {
    this.paper = new dia.Paper({
      el: this.parentContainer.firstElementChild, // Paper is always direct child of parent
      model: this.graph,
      gridSize: 16,
      drawGrid: true, 
      interactive: false,
      async: true, 
    })

    this.dropShadow = this.paper.defineFilter({
      name: 'dropShadow',
      args: {
        dx: 0,
        dy: 4,
        blur: 8,
        color: 'rgba(19, 1, 54, 0.16)'
      }
    })
  }

  setupEventListeners() {
    // Paper events
    this.paper.on('cell:pointerclick', (cellView) => this.cellClick(cellView))
    this.paper.on('cell:mouseenter', (cellView) => this.cellEnter(cellView))
    this.paper.on('cell:mouseleave', (cellView) => this.cellLeave(cellView))
    this.paper.on('blank:pointerdown', (event) => this.pointerDown(event))

    // Store bound handlers so they can be removed on disconnect
    this._onMouseMove = (e) => this.pointerMove(e)
    this._onMouseUp   = ()  => this.pointerUp()
    this._onTouchStart = (e) => this.touchStart(e)
    this._onTouchMove  = (e) => this.touchMove(e)
    this._onTouchEnd   = (e) => this.touchEnd(e)
    this._onWheel      = (e) => this.scrollWheel(e)
    this._onResize     = ()  => {
      if (this.isDragging) return
      if (this.pendingFitToView && this.hasMeasurableContainer()) {
        this.fitToView()
      }
      this.redraw()
    }
    this._onScroll     = ()  => { if (!this.isDragging) this.redraw() }

    document.addEventListener('mousemove', this._onMouseMove)
    document.addEventListener('mouseup',   this._onMouseUp)

    this.paper.el.addEventListener('touchstart', this._onTouchStart, { passive: false })
    document.addEventListener('touchmove', this._onTouchMove, { passive: false })
    document.addEventListener('touchend',  this._onTouchEnd,  { passive: false })

    // passive: false required so preventDefault() is honoured in Chrome
    this.paper.el.addEventListener('wheel', this._onWheel, { passive: false })
    window.addEventListener('resize', this._onResize)
    this.parentContainer.addEventListener('scroll', this._onScroll)

    this.parentContainer.style.cursor = 'grab'

    this.setupLayoutObserver()
  }

  hasMeasurableContainer() {
    return this.parentContainer.clientWidth > 0 && this.parentContainer.clientHeight > 0
  }

  setupLayoutObserver() {
    this.layoutObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        if (width > 0 && height > 0 && this.pendingFitToView) {
          this.fitToView()
          this.redraw()
          break
        }
      }
    })
    this.layoutObserver.observe(this.parentContainer)
  }

  // ===== NODE INTERACTIONS =====
  
  // Handle click on network map nodes to open preview in sidepanel
  cellClick(cellView) {
    const slug = cellView.model.attributes.hudu_slug
    if (!slug) return

    const isNetwork = cellView.model.attributes.hudu_type === "network"
    const path = (isNetwork ? this.previewNetworkPathValue : this.previewAssetPathValue).replace('slug', slug)
    Turbo.visit(path, { frame: 'sidepanel' })
  }

  // Apply hover effects when mouse enters a node
  cellEnter(cellView) {
    const model = cellView.model
    const labelModel = model.getEmbeddedCells()[0]
    const parentModel = model.getParentCell()
    const type = model.attributes.hudu_type
    const slug = model.attributes.hudu_slug

    if (!type) return

    if(slug) {
      model.attr('body/filter', `url(#${this.dropShadow})`)
      const targetModel = parentModel ?? model
      targetModel.attr('body/fill', `#${this.getDeviceHoverColor(type)}`)
      targetModel.attr('body/stroke', `#${this.getDeviceHoverColor(type)}`)

      if (parentModel || labelModel) {
        (parentModel ?? labelModel).attr('body/filter', `url(#${this.dropShadow})`)
      }

      return
    }
    
    // Show tooltip if no slug
    this.showNotSentTooltip(cellView, parentModel)
  }

  // Remove hover effects when mouse leaves a node
  cellLeave(cellView) {
    const model = cellView.model
    const labelModel = model.getEmbeddedCells()[0]
    const parentModel = model.getParentCell()
    const type = model.attributes.hudu_type

    if (!type) return

    model.attr('body/filter', null)
    const targetModel = parentModel ?? model
    targetModel.attr('body/fill', `#${this.getDeviceColor(type)}`)
    targetModel.attr('body/stroke', `#${type === 'network' ? this.getDeviceHoverColor(type) : this.getDeviceColor(type)}`)

    if (parentModel || labelModel) {
      (parentModel ?? labelModel).attr('body/filter', null)
    }

    // Hide tooltip
    this.hideNotSentTooltip()
  }

  // ===== TOOLTIP METHODS =====
  
  showNotSentTooltip(cellView, isLabel) {
    this.hideNotSentTooltip()
  
    const paper = this.paper
    const model = cellView.model
    const bbox = model.getBBox()
  
    const scale = this.graphScale
    const centerGraphPoint = bbox.center()
    const offsetY = 10 / scale
  
    const aboveGraphPoint = centerGraphPoint.clone()
    aboveGraphPoint.y = bbox.y - offsetY + (isLabel ? -31 : 6)
    
    const pagePoint = paper.localToPagePoint(aboveGraphPoint)
  
    const tooltipElement = document.createElement('div')
    tooltipElement.style.position = 'absolute'
    tooltipElement.style.left = `${pagePoint.x}px`
    tooltipElement.style.top = `${pagePoint.y}px`
    tooltipElement.style.pointerEvents = 'none'
    tooltipElement.style.zIndex = '1000'
    document.body.appendChild(tooltipElement)
  
    const tooltipContent = 'Device not sent to Hudu'
    
    // Show tooltip
    this.currentTooltip = tippy(tooltipElement, {
      content: tooltipContent,
      theme: 'light',
      placement: 'top',
      arrow: true,
      interactive: false,
      trigger: 'manual',
      showOnCreate: true,
      appendTo: () => document.body,
    })
  }
  
  hideNotSentTooltip() {
    if (this.currentTooltip) {
      const element = this.currentTooltip.reference;
      this.currentTooltip.destroy();
      this.currentTooltip = null;
      
      // Remove the element from the DOM
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    }
  }

  // ===== MOUSE INTERACTIONS =====
  
  // Start mouse/touch panning interaction
  pointerDown(e) {
    this.isDragging = true
    this.startX = e.clientX
    this.startY = e.clientY
    this.scrollLeft = this.parentContainer.scrollLeft
    this.scrollTop = this.parentContainer.scrollTop

    this.parentContainer.style.cursor = 'grabbing'
    document.body.style.cursor = 'grabbing'
  }

  // Handle mouse/touch movement during panning
  pointerMove(e) {
    if (!this.isDragging) return
    
    const x = e.clientX - this.startX
    const y = e.clientY - this.startY
    this.parentContainer.scrollLeft = this.scrollLeft - x
    this.parentContainer.scrollTop = this.scrollTop - y
    this.redraw()
  }

  // End mouse/touch panning interaction
  pointerUp() {
    this.isDragging = false
    this.parentContainer.style.cursor = 'grab'
    document.body.style.cursor = ''
  }

  // ===== TOUCH INTERACTIONS =====
  
  // Handle touch start events for mobile panning and pinch-to-zoom
  touchStart(e) {
    if (e.touches.length === 1) {
      this.isDragging = true
      this.isZooming = false
      this.startX = e.touches[0].clientX
      this.startY = e.touches[0].clientY
      this.scrollLeft = this.parentContainer.scrollLeft
      this.scrollTop = this.parentContainer.scrollTop
    } else if (e.touches.length === 2) {
      e.preventDefault()
      this.isDragging = false
      this.isZooming = true
      this.lastTouchDistance = this.getTouchDistance(e.touches)
      this.lastTouchCenter = this.getTouchCenter(e.touches)
    }
  }

  // Handle touch movement for panning and pinch-to-zoom gestures
  touchMove(e) {
    if (e.touches.length === 2) {
      e.preventDefault()
      
      if (!this.isZooming) {
        this.isDragging = false
        this.isZooming = true
        this.lastTouchDistance = this.getTouchDistance(e.touches)
        this.lastTouchCenter = this.getTouchCenter(e.touches)
        return
      }
      
      this.handlePinchZoom(e.touches)
    } else if (this.isDragging && e.touches.length === 1) {
      e.preventDefault()
      const x = e.touches[0].clientX - this.startX
      const y = e.touches[0].clientY - this.startY
      this.parentContainer.scrollLeft = this.scrollLeft - x
      this.parentContainer.scrollTop = this.scrollTop - y
      this.redraw()
    }
  }

  // Handle touch end events and state transitions
  touchEnd(e) {
    if (e.touches.length === 0) {
      this.isDragging = false
      this.isZooming = false
    } else if (e.touches.length === 1) {
      this.isZooming = false
      this.isDragging = true
      this.startX = e.touches[0].clientX
      this.startY = e.touches[0].clientY
      this.scrollLeft = this.parentContainer.scrollLeft
      this.scrollTop = this.parentContainer.scrollTop
    }
  }

  // Calculate distance between two touch points for pinch gestures
  getTouchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  // Calculate center point between two touch points for zoom focus
  getTouchCenter(touches) {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    }
  }

  // Handle pinch-to-zoom gesture
  handlePinchZoom(touches) {
    const currentDistance = this.getTouchDistance(touches)
    const currentCenter = this.getTouchCenter(touches)
    
    if (this.lastTouchDistance > 0) {
      const scale = currentDistance / this.lastTouchDistance
      const clampedScale = Math.max(0.5, Math.min(2.0, scale))
      const zoomFactor = (1 - clampedScale) * 0.5
      
      const currentTime = Date.now()
      const timeSinceLastZoom = currentTime - this.lastZoomTime
      
      if (Math.abs(zoomFactor) > 0.01 && timeSinceLastZoom > 16) {
        const syntheticEvent = {
          deltaY: zoomFactor * 100,
          clientX: currentCenter.x,
          clientY: currentCenter.y,
          preventDefault: () => {}
        }
        
        this.applyZoom(syntheticEvent)
        this.lastZoomTime = currentTime
      }
    }
    
    this.lastTouchDistance = currentDistance
    this.lastTouchCenter = currentCenter
  }

  // ===== ZOOM FUNCTIONALITY =====

  // Handle mouse wheel and trackpad scroll/pinch events.
  // Heuristic to distinguish zoom vs pan (no keyboard modifier required):
  //   ctrlKey=true              → macOS trackpad pinch gesture          → zoom
  //   deltaMode !== 0           → line/page mode (physical mouse wheel) → zoom
  //   deltaX === 0 && |deltaY| >= 50 → large pure-vertical pixel delta  → zoom
  //   everything else           → trackpad two-finger scroll            → pan
  scrollWheel(e) {
    e.preventDefault()

    const isZoom = e.ctrlKey ||
                   e.deltaMode !== 0 ||
                   (e.deltaX === 0 && Math.abs(e.deltaY) >= 50)

    if (isZoom) {
      this.applyZoom(e)
    } else {
      this.parentContainer.scrollLeft += e.deltaX
      this.parentContainer.scrollTop  += e.deltaY
    }
  }

  // Apply a zoom step centred on the cursor/touch position
  applyZoom(e) {
    const oldScale = this.graphScale
    if (e.deltaY < 0) {
      this.zoomIn(e)
    } else if (e.deltaY > 0) {
      this.zoomOut(e)
    }
    if (this.graphScale !== oldScale) {
      this.redraw()
    }
  }

  // Zoom in the network map
  zoomIn(e) {
    const oldScale = this.graphScale
    this.graphScale = Math.min(2, this.graphScale + 0.05)
    this.scalePaper(e, oldScale)
  }

  // Zoom out the network map
  zoomOut(e) {
    const oldScale = this.graphScale
    this.graphScale = Math.max(0.5, this.graphScale - 0.05)
    this.scalePaper(e, oldScale)
  }

  // Apply zoom scaling to the paper while maintaining focus point
  scalePaper(e, oldScale) {
    const newScale = this.graphScale

    const paperWidth = Math.max((this.graphBBox.width + 200) * newScale, this.parentContainer.clientWidth)
    const paperHeight = Math.max((this.graphBBox.height + 200) * newScale, this.parentContainer.clientHeight)

    const containerRect = this.parentContainer.getBoundingClientRect()
    const mouseX = e.clientX - containerRect.left + this.parentContainer.scrollLeft
    const mouseY = e.clientY - containerRect.top + this.parentContainer.scrollTop

    const graphX = mouseX / oldScale
    const graphY = mouseY / oldScale

    this.paper.scale(newScale, newScale)
    this.paper.setDimensions(paperWidth * Math.max(1, newScale), paperHeight * Math.max(1, newScale))

    const newMouseX = graphX * newScale
    const newMouseY = graphY * newScale

    this.parentContainer.scrollLeft = newMouseX - (e.clientX - containerRect.left)
    this.parentContainer.scrollTop = newMouseY - (e.clientY - containerRect.top)
  }

  // ===== RENDERING =====
  
  // Initial draw of the network map
  draw() {
    this.sizeTree()
    this.fitToView()
    this.drawTree(this.networkValue)
  }

  // Redraw visible elements during panning/zooming
  redraw() {
    this.graph.getElements().forEach(el => {
      if (el.attributes.type === 'standard.Rectangle') {
        el.remove()
      }
    })
    this.drawTree(this.networkValue, true)
  }

  // Recursively draw network tree nodes and connections
  drawTree(node, isRedraw) {
    const viewport = this.getViewportBounds()
    const isVisible = this.isBoxVisible(node, viewport)
    const isInternet = node.type === 'internet'

    if (isVisible && !isInternet) {
      switch (node.type) {
        case "network":
          this.drawNetworkBox(node)
          break
        default:
          this.drawDeviceBox(node)
          break
      }
    }

    if (node.children) {
      for (const child of node.children) {
        this.drawTree(child, isRedraw)
        if (!isRedraw && !isInternet) {
          this.drawLink(node, child)
        }
      }
    }
  }

  // Calculate visible area bounds for viewport culling
  getViewportBounds() {
    const containerEl = this.paper.el.parentElement
    const r = containerEl.getBoundingClientRect()

    const scaleMultiplier = (this.nodeSpacing * this.graphScale)
    const tl = this.paper.clientToLocalPoint(r.left - scaleMultiplier, r.top - scaleMultiplier)
    const br = this.paper.clientToLocalPoint(r.right + scaleMultiplier, r.bottom + scaleMultiplier)

    return {
      x1: Math.min(tl.x, br.x),
      y1: Math.min(tl.y, br.y),
      x2: Math.max(tl.x, br.x),
      y2: Math.max(tl.y, br.y)
    }
  }

  // Check if a node is within the visible viewport bounds
  isBoxVisible(node, viewport) {
    const w = node.width
    const h = node.height

    return !(
      node.x + w < viewport.x1 ||
      node.x > viewport.x2 ||
      node.y + h < viewport.y1 ||
      node.y > viewport.y2
    )
  }

  // Set paper dimensions based on network tree size.
  // Bounding box is derived from raw data rather than graph.getBBox() because
  // viewport culling in drawTree() skips off-screen nodes, causing getBBox() to
  // undercount the true extent of the graph.
  sizeTree() {
    const containerWidth = this.parentContainer.clientWidth
    const containerHeight = this.parentContainer.clientHeight

    this.graphBBox = this.getDataBBox(this.networkValue)

    const padding = this.nodeSpacing * 2
    const paperWidth  = Math.max(containerWidth,  this.graphBBox.width  + padding)
    const paperHeight = Math.max(containerHeight, this.graphBBox.height + padding)

    this.paper.setDimensions(paperWidth, paperHeight)
  }

  // Zoom out on initial load so the full graph fits within the visible container.
  fitToView() {
    const padding = this.nodeSpacing * 2
    const contentWidth = this.graphBBox.width + padding
    const contentHeight = this.graphBBox.height + padding
    const containerWidth = this.parentContainer.clientWidth
    const containerHeight = this.parentContainer.clientHeight

    if (contentWidth <= 0 || contentHeight <= 0) return

    if (containerWidth <= 0 || containerHeight <= 0) {
      this.pendingFitToView = true
      return
    }

    this.pendingFitToView = false

    const scaleX = containerWidth / contentWidth
    const scaleY = containerHeight / contentHeight
    const idealScale = Math.min(scaleX, scaleY)

    const newScale = Math.min(2, Math.max(0.5, idealScale))

    if (newScale < this.graphScale) {
      this.graphScale = newScale
      this.paper.scale(newScale, newScale)

      const paperWidth = Math.max(containerWidth, contentWidth * newScale)
      const paperHeight = Math.max(containerHeight, contentHeight * newScale)
      this.paper.setDimensions(paperWidth, paperHeight)
    }
  }

  // Recursively compute the bounding box of all nodes in the data tree,
  // regardless of whether they are currently rendered.
  getDataBBox(node, bounds = { maxX: 0, maxY: 0 }) {
    if (node.x != null && node.y != null) {
      bounds.maxX = Math.max(bounds.maxX, node.x + (node.width  || 0))
      bounds.maxY = Math.max(bounds.maxY, node.y + (node.height || 0))
    }

    if (node.children) {
      for (const child of node.children) {
        this.getDataBBox(child, bounds)
      }
    }

    return { width: bounds.maxX, height: bounds.maxY }
  }

  // ===== NODE DRAWING =====
  
  // Create a rectangle shape for network nodes
  drawRect(node) {
    return new shapes.standard.Rectangle({
      "hudu_type": node.type,
      "hudu_slug": node.slug
    })
  }

  // Draw device nodes with icon and label
  drawDeviceBox(node) {
    const rect = this.drawRect(node)
    rect.position(node.x, node.y)
    rect.resize(node.width, node.height)
    rect.attr({
      root: node.slug ? { cursor: "pointer" } : { cursor: "default" },
      body: { 
        fill: `#${this.getDeviceColor(node.type)}`, 
        rx: 12,
        ry: 12,
        stroke: `#${this.getDeviceColor(node.type)}`
      },
      label: {
        text: this.getDeviceIcon(node.type),
        fontFamily: 'FontAwesome',
        fill: '#242424',
        fontSize: 18,
      },
    })
    rect.addTo(this.graph)
    
    const labelParts = this.labelLines(node)
    const labelText = labelParts.join('\n')
    const labelRect = this.drawRect(node)
    const labelWidth = Math.max(...labelParts.map(part => this.measureText(this.paper.svg, part)))
    labelRect.position(node.x + node.width / 2 - labelWidth / 2, node.y + node.height + 5)
    const labelHeight = labelParts.length > 1 ? (labelParts.length * 15) : 20
    labelRect.resize(labelWidth, labelHeight)
    labelRect.attr({
      root: node.slug ? { cursor: "pointer" } : { cursor: "default" },
      body: { 
        fill: `#${this.getDeviceLabelColor(node.type)}`,
        stroke: `#${this.getDeviceLabelColor(node.type)}`,
        rx: 2,
        ry: 2,
      },
      label: {
        text: labelText,
        fill: '#242424',
        fontSize: 12,
      }
    })
    labelRect.addTo(this.graph)
    rect.embed(labelRect)
  }

  // Draw network nodes with label and icon
  drawNetworkBox(node) {
    const rect = this.drawRect(node)
    const labelParts = this.labelLines(node)
    const labelText = labelParts.join('\n')
    const labelWidth = Math.max(...labelParts.map(part => this.measureText(this.paper.svg, part))) + 18
    const labelHeight = labelParts.length > 1 ? (labelParts.length * 15) + 4 : 22
    rect.position(node.x + node.width / 2 - labelWidth / 2, node.y)
    rect.resize(labelWidth, labelHeight)
    rect.attr({
      root: node.slug ? { cursor: "pointer" } : { cursor: "default" },
      body: { 
        fill: `#${this.getDeviceColor(node.type)}`,
        stroke: `#${this.getDeviceHoverColor(node.type)}`,
        strokeWidth: 1,
        rx: 2,
        ry: 2,
      },
      label: {
        text: labelText,
        fill: '#242424',
        fontSize: 12,
        x: labelWidth / 2 + 10
      }
    })
    rect.addTo(this.graph)
    
    const iconRect = new shapes.standard.Rectangle()
    iconRect.position(node.x + (node.width / 2 - labelWidth / 2) + 12, node.y + 11)
    iconRect.attr({
      root: node.slug ? { cursor: "pointer" } : { cursor: "default" },
      label: { 
        text: this.getDeviceIcon(node.type),
        fill: '#242424',
        fontSize: 14,
        fontFamily: 'FontAwesome'
      }
    })
    iconRect.addTo(this.graph)
  }

  // Draw connection lines between network nodes
  drawLink(sourceNode, targetNode) {
    const link = new shapes.standard.Link({
      source: { x: sourceNode.x + (sourceNode.width / 2), y: sourceNode.y + (sourceNode.height / 2) },
      target: { x: targetNode.x + (targetNode.width / 2), y: targetNode.y + (targetNode.height / 2) }
    })
    
    link.router('orthogonal', { radius: 12 })
    link.connector('rounded')
    link.attr({
      line: {
        stroke: '#D1D1D1',
        strokeWidth: 1,
        sourceMarker: null,
        targetMarker: null,
      }
    })
    link.addTo(this.graph)
    link.toBack()
  }

  // Compose a node's display label lines. On the merged multi-collector map the
  // collector name arrives as its own field rather than baked into `name`, so it is
  // appended here. Splitting `name` is retained so maps from a hudu-services version
  // that still embeds the newline keep rendering correctly.
  labelLines(node) {
    const lines = String(node.name ?? '').split('\n')
    if (node.deployment_name) lines.push(`(${node.deployment_name})`)
    return lines
  }

  // Measure text width for label sizing
  measureText(svgDocument, text) {
    const vText = V('text').attr({fontSize: 12}).text(text)
    vText.appendTo(svgDocument)
    const bbox = vText.getBBox()
    vText.remove()
    return bbox.width + 8
  }

  // ===== STYLING =====
  
  // Get FontAwesome icon for device type
  getDeviceIcon(type) {
    const icons = {
      "network": "\uf6ff",
      "access_point": "\uf519",
      "firewall": "\ue3dc",
      "switch": "\uf796",
      "router": "\uf8da",
      "printer": "\uf02f",
      "laptop": "\uf109",
      "workstation": "\ue4e5",
      "server": "\uf233",
      "mobile": "\uf3ce",
      "network_device": "\uf5ef"
    }
    return icons[type]
  }

  // Get background color for device type
  getDeviceColor(type) {
    const colors = {
      "network": "EDE7F6BF",
      "access_point": "CE93D8",
      "firewall": "C5E1A5",
      "switch": "F48FB1",
      "router": "E6EE9C",
      "printer": "9FA8DA",
      "laptop": "A0D5D0",
      "workstation": "80DEEA",
      "server": "81D4FA",
      "mobile": "FFCC80",
      "network_device": "FFAB91"
    }
    return colors[type]
  }

  // Get label background color for device type
  getDeviceLabelColor(type) {
    const labelColors = {
      "network": "F0ECF7",
      "access_point": "F3E5F5BF",
      "firewall": "F1F8E9BF",
      "switch": "FCE4ECBF",
      "router": "F9FBE7BF",
      "printer": "E8EAF6BF",
      "laptop": "E0F2F1BF",
      "workstation": "E0F7FABF",
      "server": "E1F5FEBF",
      "mobile": "FFF3E0BF",
      "network_device": "FBE9E7BF"
    }
    return labelColors[type]
  }

  // Get hover color for device type
  getDeviceHoverColor(type) {
    const hoverColors = {
      "network": "B39DDB",
      "access_point": "BA68C8",
      "firewall": "AED581",
      "switch": "F06292",
      "router": "DCE775",
      "printer": "7986CB",
      "laptop": "80CBC4",
      "workstation": "4DD0E1",
      "server": "4FC3F7",
      "mobile": "FFB74D",
      "network_device": "FF8A65"
    }
    return hoverColors[type]
  }
}